/**
 * TermPill — Konsey Cognitive Renaissance, Bölüm B4.
 *
 * Pill + Radix Tooltip wrapper. Hover'da glossary entry'sini açıklar
 * (Türkçe başlık + 2 cümle + opsiyonel "Daha fazla" link).
 *
 * Bilinmeyen `term` → silent fallback (sadece düz Pill render edilir),
 * production'da hata fırlatmaz.
 *
 * Kullanım:
 *   <TermPill term="deadlock" tone="warn">Deadlock</TermPill>
 *   <TermPill term="sycophancy">Aşırı Uyum</TermPill>
 */
import type { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Pill } from "./Pill";
import type { PillProps } from "./Pill";
import { getGlossaryEntry } from "../../i18n/glossary";

export interface TermPillProps extends Omit<PillProps, "children"> {
  /** Glossary key — case-insensitive, hyphen→underscore. */
  term: string;
  /** Override Pill children (varsa); yoksa glossary `tr` başlığı kullanılır. */
  children?: ReactNode;
  /** Tooltip yan tarafı — default top. */
  side?: "top" | "right" | "bottom" | "left";
}

export function TermPill({
  term,
  children,
  side = "top",
  ...pillProps
}: TermPillProps) {
  const entry = getGlossaryEntry(term);

  // Bilinmeyen terim → düz Pill (silent fallback)
  if (!entry) {
    return <Pill {...pillProps}>{children ?? term}</Pill>;
  }

  const label = children ?? entry.tr;

  return (
    <TooltipPrimitive.Root delayDuration={250} disableHoverableContent={false}>
      <TooltipPrimitive.Trigger asChild>
        <span
          className="inline-flex cursor-help"
          tabIndex={0}
          aria-label={`${entry.tr}: ${entry.desc}`}
        >
          <Pill {...pillProps}>{label}</Pill>
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          align="center"
          // z-[400] modal z-[201]'in açıkça üstünde — BrainModal
          // içindeki TermPill tooltip'leri arkada kalmasın.
          className="z-[400] max-w-xs rounded-lg border border-white/[0.10] bg-[hsl(220_22%_8%/0.96)] px-3 py-2 text-[11.5px] leading-relaxed text-fg-strong backdrop-blur-xl shadow-[0_8px_28px_-8px_rgb(0_0_0/0.7)] data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0"
        >
          <div className="mb-1 flex items-center gap-2">
            <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-accent-300">
              Terim
            </span>
            <span className="text-sm font-semibold text-white">{entry.tr}</span>
          </div>
          <p className="text-[12px] text-fg-muted">{entry.desc}</p>
          {entry.more ? (
            <a
              href={entry.more}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-accent-300 hover:text-accent-200 transition-colors"
            >
              Daha fazla →
            </a>
          ) : null}
          <TooltipPrimitive.Arrow className="fill-[hsl(220_22%_8%)] stroke-white/[0.10]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
